"use client";

import { Check, Instagram, Facebook, Twitter, ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Controller } from "react-hook-form";
function ConnectForm() {
  const {
    control,
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-24 min-h-[400px]">
        <div className="size-14 rounded-lg border border-[var(--primary)] flex items-center justify-center mb-8 animate-in fade-in zoom-in duration-700">
          <Check className="size-6 text-[var(--primary)]" strokeWidth={1.5} />
        </div>
        <p className="font-default italic text-4xl md:text-5xl leading-tight text-foreground animate-in fade-in slide-in-from-bottom-4 duration-700">
          Thank you.
          <br />
          Your message has been received.
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Please share your name",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.25em] text-muted-foreground font-default font-normal">
              Your Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 pb-3 text-lg font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-[var(--primary)] transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.25em] text-muted-foreground font-default font-normal">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 pb-3 text-lg font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-[var(--primary)] transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.25em] text-muted-foreground font-default font-normal">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="h-auto border-0 border-b border-border bg-transparent px-0 pb-3 text-lg font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-[var(--primary)] transition-colors" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormItem className="gap-3">
        <FormLabel className="text-[0.65rem] uppercase tracking-[0.25em] text-muted-foreground font-default font-normal">
          Subject
        </FormLabel>
        <Controller control={control} name="subject" render={({
        field
      }) => <Select onValueChange={field.onChange} value={field.value || ""}>
              <SelectTrigger className="h-auto border-0 border-b border-border bg-transparent px-0 pb-3 text-lg font-default text-foreground focus-visible:ring-0 focus-visible:border-[var(--primary)] data-[placeholder]:text-muted-foreground/50 w-full transition-colors [&_svg]:text-muted-foreground">
                <SelectValue placeholder="e.g., Volunteering Inquiry" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="volunteering">Volunteering</SelectItem>
                <SelectItem value="donations">Donations</SelectItem>
                <SelectItem value="refuge-info">Refuge Information</SelectItem>
                <SelectItem value="events">Events</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>} />
      </FormItem>

      <FormField control={control} name="message" rules={{
      required: "Please share a message",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="text-[0.65rem] uppercase tracking-[0.25em] text-muted-foreground font-default font-normal">
              Message
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="How can we help you protect our wildlife?" {...field} className="border-0 border-b border-border bg-transparent px-0 pb-3 text-lg font-default text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-[var(--primary)] resize-none transition-colors min-h-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 rounded-md">
          <p className="text-sm text-destructive font-default">{submitError}</p>
        </div>}

      <div className="pt-6">
        <Button type="submit" variant="outline" disabled={isSubmitting} className="w-full md:w-auto border border-[var(--primary)] bg-transparent text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--primary-foreground)] rounded-lg px-10 py-6 h-auto text-sm font-default uppercase tracking-[0.2em] transition-colors group">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending
            </> : <>
              Send Message
              <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
            </>}
        </Button>
        <p className="mt-6 text-xs font-default text-muted-foreground/70 tracking-wide">
          We aim to respond within two business days.
        </p>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const contactRows = [{
    label: "Email",
    value: "protect@refuges.org"
  }, {
    label: "Phone",
    value: "(360) 555-0199"
  }, {
    label: "Office",
    value: "Port Angeles, WA"
  }];
  const socials = [{
    name: "Instagram",
    icon: Instagram,
    href: "#"
  }, {
    name: "Facebook",
    icon: Facebook,
    href: "#"
  }, {
    name: "Twitter",
    icon: Twitter,
    href: "#"
  }];
  return <section data-section-id="3863" id="direct-inquiry" className="dark bg-background text-foreground pt-32 pb-32 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
          <div ref={leftRef} className={`lg:col-span-6 lg:pl-8 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[0.7rem] uppercase tracking-[0.35em] text-[var(--primary)] font-default mb-12">
              Advocacy &mdash; Contact
            </p>

            <h1 className="font-default text-foreground leading-[0.95] mb-10" style={{
            fontSize: "clamp(3rem, 6vw, 5rem)"
          }}>
              Protecting
              <br />
              our <span className="italic">refuges.</span>
            </h1>

            <p className="font-default text-lg md:text-xl text-muted-foreground leading-relaxed max-w-[420px] mb-16">
              Have questions about Protection Island or Dungeness? Reach out to learn how you can help preserve our vital wildlife habitats.
            </p>

            <div className="max-w-[460px] mb-20">
              {contactRows.map((row, i) => <div key={i} data-index={i} className="flex items-baseline justify-between py-6 border-t border-border last:border-b">
                  <span className="text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground font-default">
                    {row.label}
                  </span>
                  <span className="font-default italic text-lg md:text-xl text-foreground">
                    {row.value}
                  </span>
                </div>)}
            </div>

            <div className="flex items-center gap-5">
              <span className="text-[0.65rem] uppercase tracking-[0.3em] text-muted-foreground font-default mr-2">
                Follow
              </span>
              {socials.map((social, i) => {
              const Icon = social.icon;
              return <Link key={social.name} data-index={i} to={social.href} aria-label={social.name} className="size-9 border border-border rounded-lg flex items-center justify-center text-muted-foreground hover:text-[var(--primary)] hover:border-[var(--primary)] transition-colors">
                    <Icon className="size-4" strokeWidth={1.25} />
                  </Link>;
            })}
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-6 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="direct_inquiry_form_get_involved" formId="direct_inquiry_form_get_involved" sectionName="direct-inquiry" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you. Your message has been received." errorMessage="We could not send your message. Please try again.">
              <ConnectForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}